
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import MarketWithExchange from "@/models/MarketWithExchange";
import ThemeUpdatingView from "@/components/general/ThemeUpdatingView.vue";

/**
 * Model which represents a single entry in the Ticker
 */
class FormatedPair {
  public proName: string;
  constructor(exchange: string, proName: string) {
    this.proName =
      exchange
        .split(".")
        .join("")
        .toUpperCase() +
      ":" +
      proName;
  }
}

/**
 * Wrapper component which handles the dynamic addition and removal of markets depending on the running bots
 */
@Component
export default class TradingViewTicker extends ThemeUpdatingView {
  // 📦 Unique markets with exchanges
  @Prop()
  public selectedMarketsWithExchanges!: MarketWithExchange;

  // 📦 Formatted pairs of exchange and market that are passed to the ticker
  private formattedPairs: FormatedPair[] | null = null;

  // 🚩 Flag for whether the ticker should be shown based on the exchange
  private get shouldShowTicker(): boolean {
    switch (this.selectedMarketsWithExchanges.exchange) {
      case "Bitvavo":
        return false;
      default:
        return true;
    }
  }

  /**
   * 👂 Listener for changes in the provided pairs
   */
  @Watch("selectedMarketsWithExchanges", { immediate: true })
  private onSelectedMarketsWithExchangesChanged(
    val: MarketWithExchange,
    oldVal?: MarketWithExchange
  ) {
    if (this.formattedPairs != null && this.formattedPairs.length > 0) {
      return;
    }
    this.formatPairs();
  }

  protected mounted() {
    this.formatPairs();
  }

  protected themeUpdate() {
    (this.$refs.tickerWidget as any).innerHTML = "";
    this.formatPairs();
  }

  /**
   * 🔨 Formats the exchange + market so it can be attached to the TradingView ticker
   */
  private formatPairs() {
    this.formattedPairs = [
      new FormatedPair(
        this.formatExchange(this.selectedMarketsWithExchanges.exchange),
        this.selectedMarketsWithExchanges.market.split(":")[0] +
          this.selectedMarketsWithExchanges.market.split(":")[1]
      ),
    ];
    if (
      this.formattedPairs.length > 0 &&
      this.$refs.tickerWidget !== undefined
    ) {
      this.loadTickerScript();
    }
  }

  /**
   * 🔨 Maps the exchange name to a TradingView readable exchange
   */
  private formatExchange(exchange: string) {
    switch (exchange) {
      case "Bitpanda Pro":
        return "BitpandaPro";
      case "Coinbase Pro":
        return "Coinbase";
      case "Binance.US":
        return "Binance US";
      case "Crypto.com":
        return "Binance";
      default:
        return exchange;
    }
  }

  /**
   * 🔨 Loads the TradingView Ticker script dynamically
   */
  private loadTickerScript() {
    let tickerScript = document.createElement("script");
    tickerScript.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-tickers.js";
    tickerScript.async = true;
    tickerScript.type = "text/javascript";
    tickerScript.innerHTML =
      '{ "symbols": ' +
      JSON.stringify(this.formattedPairs).replace(/\\"/g, '"') +
      ',"colorTheme": "' +
      (this.$vuetify.theme.dark ? "dark" : "light") +
      '", "isTransparent": true, "showSymbolLogo": true, "locale": "en"}';
    (this.$refs.tickerWidget as any).appendChild(tickerScript);
  }
}
